// Call restrictions on the SIP side (contract §5, §6). Calls from the web
// panel carry X-PHQ-Client so routing rejects a refused destination at once
// with a 403 and the reason headers, instead of playing the announcement.

export const PHQ_CLIENT_HEADER = 'X-PHQ-Client';
export const REFUSAL_HEADER = 'X-PHQ-Refusal';
export const REFUSAL_SOURCE_HEADER = 'X-PHQ-Refusal-Source';
export const REFUSAL_LABEL_HEADER = 'X-PHQ-Refusal-Label';
export const DESTINATION_NOT_AUTHORIZED = 'destination_not_authorized';

/** `X-PHQ-Client: web/1.4.2`; `web/unknown` when the build has no version. */
export const phqClientHeader = (version: string | undefined): string =>
    `${PHQ_CLIENT_HEADER}: web/${version && version.trim() ? version.trim() : 'unknown'}`;

export interface SipRefusal {
    /** global | phonehq_default | tenant | company | group | person | price_limit, or undefined. */
    source?: string;
    /** Decoded label, e.g. "Polish premium-rate numbers". */
    label?: string;
}

interface SipResponseLike {
    status_code?: number;
    getHeader?: (name: string) => unknown;
}

const header = (response: SipResponseLike, name: string): string | undefined => {
    if (typeof response.getHeader !== 'function') return undefined;
    try {
        const value = response.getHeader(name);
        return typeof value === 'string' && value.trim() ? value.trim() : undefined;
    } catch {
        return undefined;
    }
};

/** Percent-encoded UTF-8; a malformed value is shown as it came rather than dropped. */
export const decodeRefusalLabel = (raw: string | undefined): string | undefined => {
    if (!raw) return undefined;
    try {
        return decodeURIComponent(raw).trim() || undefined;
    } catch {
        return raw;
    }
};

/**
 * The refusal carried by a final response, or undefined for any other
 * failure (busy, unavailable, a plain 403…). Falls back to the Reason header
 * text when a proxy dropped the X-PHQ-* headers.
 */
export const parseSipRefusal = (response: unknown): SipRefusal | undefined => {
    if (!response || typeof response !== 'object') return undefined;
    const sip = response as SipResponseLike;
    if (sip.status_code !== 403) return undefined;
    const refusal = header(sip, REFUSAL_HEADER)?.toLowerCase();
    const reason = header(sip, 'Reason')?.toLowerCase() ?? '';
    if (refusal !== DESTINATION_NOT_AUTHORIZED && !reason.includes(DESTINATION_NOT_AUTHORIZED)) {
        return undefined;
    }
    return {
        source: header(sip, REFUSAL_SOURCE_HEADER)?.toLowerCase(),
        label: decodeRefusalLabel(header(sip, REFUSAL_LABEL_HEADER)),
    };
};
